import React, { useMemo, useCallback } from 'react'
import { useTheme } from '@mui/material/styles'
import {
  Box,
  Chip,
  IconButton,
  CircularProgress,
  Typography,
  capitalize,
} from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { GetApp as DownloadIcon } from '@mui/icons-material'
import dayjs from 'dayjs'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { CertificatesRollupData } from 'src/v3/profile/v4/certificates/types'
import { useCertificateDownload } from 'src/v3/profile/v4/certificates/hooks/use-certificate-download'

interface CertificateCounts {
  recorded: number
  live: number
  external: number
  event: number
}

interface TransformedCertificate {
  id: string
  certificateID: string | null
  courseActivity: string
  completionDate: string
  ceHours: number
  accreditationStatus: string
  category: string
  type: 'recorded' | 'live' | 'external' | 'attendance' | 'event'
  courseCode: string | null
  assignment: boolean
  assignmentName: string | null
}

interface UseCertificateDataReturn {
  certificateCounts: CertificateCounts
  transformedCertificates: TransformedCertificate[]
  certificateColumns: GridColDef[]
  CustomToolbar: () => React.JSX.Element
  isLoading: boolean
  hasError: boolean
}

export function useCertificateData(
  selectedUserCertificates: CertificatesRollupData | undefined,
  isLoading: boolean = false,
  hasError: boolean = false,
  downloadHook:
    | {
        downloadCertificate: (
          item: any,
        ) => Promise<{ success: boolean; error?: string }>
        isDownloading: (id: string | null) => boolean
      }
    | undefined = undefined,
): UseCertificateDataReturn {
  let theme = useTheme()
  let defaultDownloadHook = useCertificateDownload()
  let { downloadCertificate, isDownloading } =
    downloadHook || defaultDownloadHook

  // Certificate download cell component
  let CertificateDownloadCell = useCallback(
    ({ item }: { item: TransformedCertificate }) => {
      let handleDownload = async (e: React.MouseEvent) => {
        e.stopPropagation()
        let result = await downloadCertificate(item)
        if (!result.success && result.error) {
          // Could show a toast notification here in the future
          // eslint-disable-next-line no-console
          console.error('Certificate download failed:', result.error)
        }
      }

      if (!item.certificateID || item.type === 'attendance') {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              N/A
            </Typography>
          </Box>
        )
      }

      let isCurrentlyDownloading = isDownloading(item.certificateID)

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <IconButton
            size="small"
            onClick={handleDownload}
            disabled={isCurrentlyDownloading}
            aria-label={`Download certificate for ${item.courseActivity}`}
            sx={{ color: 'primary.main' }}
          >
            {isCurrentlyDownloading ? (
              <CircularProgress size={20} />
            ) : (
              <DownloadIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      )
    },
    [downloadCertificate, isDownloading],
  )

  // Memoized certificate counts
  let certificateCounts = useMemo(() => {
    if (!selectedUserCertificates?.length) {
      return {
        recorded: 0,
        live: 0,
        external: 0,
        event: 0,
      }
    }

    return selectedUserCertificates.reduce(
      (counts, cert) => ({
        ...counts,
        recorded: counts.recorded + (cert.type === 'recorded' ? 1 : 0),
        live: counts.live + (cert.type === 'live' ? 1 : 0),
        external: counts.external + (cert.type === 'external' ? 1 : 0),
        event: counts.event + (cert.type === 'event' ? 1 : 0),
      }),
      { recorded: 0, live: 0, external: 0, event: 0 },
    )
  }, [selectedUserCertificates])

  // Memoized transformed certificates for DataGrid with type safety
  let transformedCertificates = useMemo((): TransformedCertificate[] => {
    if (!selectedUserCertificates || hasError) return []

    return selectedUserCertificates.map((item, index) => ({
      ...item,
      id: `${item.certificateID || 'no-cert'}-${index}`,
      // Ensure required fields have fallbacks and proper types
      certificateID: item.certificateID || null,
      courseActivity: item.courseActivity || 'Unknown Course',
      completionDate: item.completionDate || '',
      ceHours: item.ceHours || 0,
      category: item.category || 'Uncategorized',
      type: item.type || 'recorded',
      courseCode: item.courseCode || null,
      assignment: item.assignment || false,
      assignmentName: item.assignmentName || null,
    }))
  }, [selectedUserCertificates, hasError])

  // Memoized certificate columns configuration
  let certificateColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'courseActivity',
        headerName: 'Course/Activity',
        flex: 1,
        minWidth: 250,
        sortable: true,
      },
      {
        field: 'certificateID',
        headerName: 'Certificate',
        width: 100,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          let item = params.row as TransformedCertificate
          return <CertificateDownloadCell item={item} />
        },
        valueGetter: () => 'Download Certificate',
      },
      {
        field: 'completionDate',
        headerName: 'Completion Date',
        width: 150,
        sortable: true,
        type: 'date',
        valueGetter: (value: string) =>
          value ? dayjs(value).toDate() : undefined,
        valueFormatter: (value: Date | undefined) =>
          value ? dayjs(value).format('MMM DD, YYYY') : '',
      },
      {
        field: 'ceHours',
        headerName: 'CE Hours',
        width: 100,
        type: 'number',
        sortable: true,
        renderCell: (params) => (
          <Box sx={{ color: theme.palette.entityColors.ceActivity }}>
            {params.value}
          </Box>
        ),
      },
      {
        field: 'accreditationStatus',
        headerName: 'Accreditation Status',
        width: 150,
        sortable: true,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value}
            variant="outlined"
            sx={{
              backgroundColor: 'transparent',
              borderColor: 'divider',
              color: 'text.secondary',
            }}
          />
        ),
      },
      {
        field: 'category',
        headerName: 'Category',
        width: 180,
        sortable: true,
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 120,
        sortable: true,
        renderCell: (params) => (
          <Chip
            size="medium"
            label={capitalize(params.value)}
            variant="outlined"
            sx={{
              backgroundColor: 'transparent',
              borderColor: 'divider',
              color: 'text.secondary',
            }}
          />
        ),
      },
      {
        field: 'courseCode',
        headerName: 'Course Code',
        width: 120,
        sortable: true,
        renderCell: (params) => <span>{params.value || '-'}</span>,
      },
      {
        field: 'assignment',
        headerName: 'Was Assigned',
        width: 120,
        sortable: true,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? 'Yes' : 'No'}
            variant="outlined"
            sx={{
              backgroundColor: 'transparent',
              borderColor: 'divider',
              color: 'text.secondary',
            }}
          />
        ),
      },
      {
        field: 'assignmentName',
        headerName: 'Assignment Name',
        width: 200,
        sortable: true,
        renderCell: (params) => <span>{params.value || '-'}</span>,
      },
    ],
    [theme.palette.entityColors.ceActivity, CertificateDownloadCell],
  )

  // Memoized custom toolbar component
  let CustomToolbar = useCallback(
    () => <DataGridToolbar fileName="user-certificates-learning-summary" />,
    [],
  )

  return {
    certificateCounts,
    transformedCertificates,
    certificateColumns,
    CustomToolbar,
    isLoading,
    hasError,
  }
}
